//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Types
import type { FlaggingRequestItem } from "../../../services/api/domains/lis/lis.types";
import type { useValidationFilters } from "./useValidationFilters";
import type { useValidationSelection } from "./useValidationSelection";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Validation Data Fetching Composable
 *
 * Orchestrates all data loading operations with race condition protection.
 *
 * Responsibilities:
 * - Fetch samples list with current filter criteria
 * - Fetch individual sample data (results + visit + flagging)
 * - Prevent race conditions when user rapidly switches samples
 * - Coordinate parallel API calls (results + visit) for speed
 * - Calculate flagging in background (non-blocking UI)
 * - Respect cache to avoid redundant API calls
 * - Abort stale requests when selection changes
 *
 * @param key - Unique key for state isolation
 * @param pendingComposable - Pending data source
 * @param lisComposable - LIS data source
 * @param filtersComposable - Active filters
 * @param selectionComposable - Selection state
 * @param getSelectionId - Current selection ID getter (for validation)
 */
export const useValidationDataFetching = (
  key: string,
  pendingComposable: ReturnType<typeof usePending>,
  lisComposable: ReturnType<typeof useLis>,
  filtersComposable: ReturnType<typeof useValidationFilters>,
  selectionComposable: ReturnType<typeof useValidationSelection>,
  getSelectionId: () => number,
) => {
  //
  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================
  //

  /**
   * Calculate flagging in background (non-blocking)
   *
   * Purpose:
   * - Compute clinical flags (warnings/alerts) for laboratory results
   * - Run asynchronously to avoid blocking UI rendering
   * - Display results immediately, show flags shortly after
   *
   * Responsibilities:
   * - Build flagging request from results and patient demographics
   * - Call flagging API with result values and patient context
   * - Validate selection ID to prevent race conditions
   * - Update flagging state only if selection hasn't changed
   * - Respect cache unless forceRefresh is true
   *
   * @param results - Pending results to flag
   * @param visit - Visit with patient demographics
   * @param selectionId - Selection ID at fetch start (for validation)
   * @param barcode - Sample barcode (for cache key)
   * @param forceRefresh - Skip cache if true
   */
  const calculateFlaggingInBackground = async (
    results: any[],
    visit: any,
    selectionId: number,
    barcode: string,
    forceRefresh: boolean,
  ): Promise<void> => {
    try {
      // Build flagging request from results
      const flaggingRequest: FlaggingRequestItem[] = results.map((r) => ({
        patient: { born: visit.patient.born, isMale: visit.patient.isMale },
        test: { id: r.mappingKey, result: r.displayResult },
        refId: r.id.toString(),
      }));

      // Always fetch fresh flags (forceRefresh=true) so every sample visit
      // gets up-to-date flagging. We still pass barcode as the cache key so
      // that clearFlaggingCache() can purge entries by barcode on full refresh.
      const flagging = await lisComposable.calculateFlagging(flaggingRequest, barcode, forceRefresh);

      // Only update if selection hasn't changed
      if (getSelectionId() === selectionId && flagging) {
        selectionComposable.selectedSampleFlagging.value = flagging;
      }
    } catch (error) {
      console.error("[calculateFlaggingInBackground] Error:", error);
    }
  };

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  /**
   * Fetch samples with current filters
   *
   * Purpose:
   * - Load pending samples from backend with active filter criteria
   * - Support both visible and silent (background) fetching
   * - Respect or bypass cache based on forceRefresh flag
   *
   * Responsibilities:
   * - Convert UI filters to API format
   * - Call pending composable with filter parameters
   * - Show/hide loading indicator based on silent flag
   * - Cache results for subsequent calls
   *
   * @param forceRefresh - Skip cache and fetch fresh data (default: false)
   * @param silent - Don't show loading indicator (default: false)
   */
  const fetchSamples = async (forceRefresh = false, silent = false): Promise<void> => {
    await pendingComposable.fetchPendingSamples(filtersComposable.apiFilters.value, forceRefresh, silent);
  };

  /**
   * Fetch sample data (results + visit + flagging)
   *
   * Purpose:
   * - Load complete data for selected sample in one coordinated operation
   * - Fetch results and visit in parallel for performance
   * - Calculate flags asynchronously without blocking UI
   * - Protect against race conditions when user rapidly switches samples
   *
   * Stale-while-revalidate strategy:
   * - If cached data exists for the sample → paint it to the panel immediately
   *   (zero perceived latency), then re-fetch from the network in the background.
   *   When the fresh response arrives the panel updates silently, no spinner shown.
   * - If no cached data → show loading overlay, wait for network response, paint.
   * - `isSilent = true` (background/SignalR refresh) → always suppresses the overlay,
   *   never clears displayed data.
   *
   * Flagging strategy:
   * - Existing flags are kept visible during silent/cache refreshes.
   * - Flags are only cleared when there are none yet, preventing a "unflagged"
   *   flash while recalculation runs.
   *
   * Flow:
   * 1.  Capture selection ID (race condition guard)
   * 2.  Abort pending requests if NOT a silent refresh
   * 3.  Check API cache for results and visit
   * 4.  If cached → paint to panel immediately, mark as hasCachedDisplay
   * 5.  If nothing to show → show loading overlay (unless isSilent)
   * 6.  Fetch results and visit from network in parallel
   * 7.  Validate selection ID (abort if changed)
   * 8.  Update panel with fresh network data
   * 9.  Start background flagging recalculation
   * 10. Clear loading flag
   *
   * @param barcode - Sample barcode to fetch
   * @param forceRefresh - Skip cache and fetch fresh data (default: false)
   * @param isSilent - Silent background refresh, no overlay (default: false)
   */
  const fetchSampleData = async (barcode: string, forceRefresh = false, isSilent = false): Promise<void> => {
    // Capture selection ID at start
    const selectionId = getSelectionId();

    // Only abort if NOT a silent refresh
    if (!isSilent) {
      pendingComposable.abortAll();
      lisComposable.abortAll();
    }

    // -------------------------------------------------------------------------
    // STEP 1: Check cache and decide overlay strategy
    // -------------------------------------------------------------------------
    // Read directly from the API-layer cache (sessionStorage-backed, survives
    // page refresh).

    const cachedResults = !forceRefresh ? pendingComposable.sampleResults.value[barcode.toUpperCase()] : undefined;
    const cachedVisit = !forceRefresh ? lisComposable.visitCache.value[barcode.toUpperCase()] : undefined;
    // hasCachedVisit: key present in cache (even if null = 404 / no visit found)
    const hasCachedVisit = !forceRefresh && lisComposable.hasCachedVisitFor(barcode);

    // Show overlay when:
    // - visit not in cache at all (cold)
    // - visit cached as null (might be stale 404 — re-validate but cover with overlay)
    // - forceRefresh bypasses all cache
    // Only a warm non-null visit skips the overlay; network re-fetch runs silently.
    const needsOverlay = !isSilent && (!hasCachedVisit || cachedVisit === null || forceRefresh);

    if (!isSilent) {
      selectionComposable.loadingSampleData.value = needsOverlay;

      // Clear stale flags from previous sample only when the overlay will hide them.
      // For warm-cache (no overlay), leave existing flags visible until fresh ones arrive —
      // this avoids a brief "no flags" flash on cached samples.
      if (needsOverlay) {
        selectionComposable.selectedSampleFlagging.value = [];
      }
    }

    // -------------------------------------------------------------------------
    // STEP 2: Paint cached data immediately (stale-while-revalidate)
    // -------------------------------------------------------------------------
    // Populate the panel synchronously from cache so data is ready the instant
    // the overlay lifts. Spread-copy to break shared reactive proxy references:
    // assigning sampleResults.value[key] directly makes selectedSampleResults
    // share the same reactive object as the global cache, so later cache writes
    // for OTHER barcodes corrupt the displayed data.
    //
    // Only the fully-warm + non-null-visit path skips the overlay. All other
    // branches (cold, partial, null-visit) run under the overlay.

    if (cachedResults?.length && hasCachedVisit && cachedVisit !== null) {
      // Fully warm: paint results, visit, and flagging atomically.
      // All three are synchronous reads so Vue batches them into one render.
      if (getSelectionId() === selectionId) {
        selectionComposable.selectedSampleResults.value = [...cachedResults];
        selectionComposable.selectedSampleVisit.value = { ...cachedVisit! } as NonNullable<
          typeof selectionComposable.selectedSampleVisit.value
        >;
        // right away. Background flagging re-fetch will update silently.
        const cachedFlagging = cachedVisit!.patient ? lisComposable.getCachedFlaggingFor(barcode, cachedResults) : null;
        if (cachedFlagging) {
          selectionComposable.selectedSampleFlagging.value = cachedFlagging;
        }
      }
    } else if (!cachedResults?.length && !hasCachedVisit) {
      // Cold cache — clear stale data; overlay is shown.
      // Skip in silent mode: we must not wipe displayed results/flags while a
      // background (SignalR-triggered) refresh is in flight. The freshly fetched
      // data will overwrite state momentarily.
      if (!isSilent) {
        selectionComposable.clearData();
      }
    } else {
      // Partial cache or null-visit — paint whatever is warm (under overlay)
      if (getSelectionId() === selectionId) {
        if (cachedResults?.length) {
          selectionComposable.selectedSampleResults.value = [...cachedResults];
        }
        if (hasCachedVisit && cachedVisit !== null) {
          selectionComposable.selectedSampleVisit.value = { ...cachedVisit! } as NonNullable<
            typeof selectionComposable.selectedSampleVisit.value
          >;
        }
      }
    }

    // -------------------------------------------------------------------------
    // STEP 3: Fetch fresh data from network (always, regardless of cache)
    // -------------------------------------------------------------------------

    try {
      const [results, visit] = await Promise.all([
        pendingComposable.fetchPendingSampleResults(barcode, forceRefresh),
        lisComposable.fetchVisitBySample(barcode, forceRefresh),
      ]);

      // Both null = both aborted (rapid switching) — leave whatever is displayed
      if (results === null && visit === null) {
        return;
      }

      // Verify this fetch is still the current selection
      if (getSelectionId() !== selectionId) {
        return;
      }

      // Update visit — but ONLY overwrite with null when there was no cached
      // visit to begin with. fetchVisitBySample returns null for three different
      // reasons: 404 (authoritative), abort, and any other error. If we had a
      // cached visit and the network returns null (likely an abort or transient
      // error), we must not wipe valid cached data.
      if (visit !== null) {
        selectionComposable.selectedSampleVisit.value = visit;
      } else if (!hasCachedVisit) {
        // No cache existed → null is authoritative (404 or confirmed no visit)
        selectionComposable.selectedSampleVisit.value = null;
      }
      // else: visit===null but hasCachedVisit → keep the cached value already painted

      if (results !== null) {
        if (results.length > 0) {
          selectionComposable.selectedSampleResults.value = results;
        } else {
          // Server confirmed empty — clear panel
          selectionComposable.clearData();
          return;
        }
      }
      // results===null: error or partial abort — keep cached data visible

      // Kick off flagging in background (fire-and-forget, never blocks overlay).
      // Respects forceRefresh: for normal navigation (false) the in-memory
      // flaggingCache in useLis is hit immediately and returns with no network
      // call, so flags are confirmed/updated silently. Only an explicit full
      // refresh (true) bypasses the cache and goes to the network.
      const finalResults = selectionComposable.selectedSampleResults.value;
      const finalVisit = selectionComposable.selectedSampleVisit.value;
      if (finalVisit?.patient && finalResults.length > 0) {
        calculateFlaggingInBackground(finalResults, finalVisit, selectionId, barcode, forceRefresh);
      }
    } catch (err) {
      // Errors are already handled in nested composables.
      // Only clear data if nothing was painted from cache.
      if (getSelectionId() === selectionId && selectionComposable.selectedSampleResults.value.length === 0) {
        selectionComposable.clearData();
      }
    } finally {
      // Always clear the loading flag — even on error or abort.
      // Flagging is fire-and-forget and never holds this flag.
      if (getSelectionId() === selectionId) {
        selectionComposable.loadingSampleData.value = false;
      }
    }
  };

  return {
    // Methods
    fetchSamples,
    fetchSampleData,
  };
};
